import {
    HttpErrorResponse,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
} from '@angular/common/http'
import { inject } from '@angular/core'
import { toast } from '@spartan-ng/brain/sonner'
import { TokenStorageService } from '@touhidrahman/ngx-common'
import { catchError, switchMap, throwError } from 'rxjs'
import { AuthStateService } from '../auth/auth-state.service'

export const isPublicAuthEndpoint = (url: string): boolean => {
    return (
        url.includes('/auth/token') ||
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/auth/reset-password') ||
        url.includes('/auth/verify-email') ||
        url.includes('/auth/change-password') ||
        url.includes('/auth/restauth-groups')
    )
}

export const TokenInterceptorFn: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
) => {
    const authStateService = inject(AuthStateService)
    const tokenStorageService = inject(TokenStorageService)

    if (isPublicAuthEndpoint(req.url)) return next(req)

    const attachToken = (
        request: HttpRequest<unknown>,
        token: string | null,
    ) =>
        token
            ? request.clone({
                  setHeaders: { Authorization: `Bearer ${token}` },
              })
            : request

    const accessToken = tokenStorageService.getAccessToken()

    if (!accessToken) {
        return next(req)
    }

    if (authStateService.isAccessTokenExpiringIn(60)) {
        return authStateService.refreshAccessToken().pipe(
            switchMap((newToken) => {
                if (!newToken) {
                    const refreshToken = tokenStorageService.getRefreshToken()
                    if (authStateService.isTokenExpired(refreshToken)) {
                        authStateService.logout('/login')
                        toast.error('Session expired. Please login again.')
                        return throwError(
                            () =>
                                new HttpErrorResponse({
                                    error: 'Session expired',
                                    status: 401,
                                    statusText: 'Unauthorized',
                                    url: req.url,
                                }),
                        )
                    }
                    return next(attachToken(req, accessToken))
                }
                return next(attachToken(req, newToken))
            }),
            catchError((err) => {
                const refreshToken = tokenStorageService.getRefreshToken()
                if (authStateService.isTokenExpired(refreshToken)) {
                    authStateService.logout('/login')
                    toast.error('Session expired. Please login again.')
                }
                return throwError(() => err)
            }),
        )
    }
    return next(attachToken(req, accessToken)).pipe(
        catchError((err: HttpErrorResponse) => {
            if (err.status !== 401) return throwError(() => err)

            return authStateService.refreshAccessToken().pipe(
                switchMap((newToken) => {
                    if (!newToken) {
                        const refreshToken =
                            tokenStorageService.getRefreshToken()
                        if (authStateService.isTokenExpired(refreshToken)) {
                            authStateService.logout('/login')
                            toast.error('Session expired. Please login again.')
                        }
                        return throwError(() => err)
                    }

                    return next(attachToken(req, newToken))
                }),
                catchError((refreshErr) => {
                    const refreshToken = tokenStorageService.getRefreshToken()
                    if (authStateService.isTokenExpired(refreshToken)) {
                        authStateService.logout('/login')
                        toast.error('Session expired. Please login again.')
                    }
                    return throwError(() => refreshErr || err)
                }),
            )
        }),
    )
}
